import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BookOpen, Printer, Loader2 } from "lucide-react";

function inline(text) {
  const parts = [];
  let rest = text, key = 0;
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/;
  let m;
  while ((m = rest.match(re))) {
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    if (m[2] !== undefined) parts.push(<strong key={key++} className="font-semibold text-slate-900">{m[2]}</strong>);
    else parts.push(<code key={key++} className="px-1 py-0.5 rounded bg-slate-100 text-[#12346B] text-[0.85em]">{m[3]}</code>);
    rest = rest.slice(m.index + m[0].length);
  }
  if (rest) parts.push(rest);
  return parts;
}

function renderMarkdown(md) {
  const lines = md.split("\n");
  const out = [];
  let i = 0, key = 0, list = null;
  const flush = () => {
    if (!list) return;
    const Tag = list.type;
    out.push(<Tag key={key++} className={`${Tag === "ul" ? "list-disc" : "list-decimal"} pl-6 space-y-1 my-3 text-slate-700`}>{list.items.map((it, x) => <li key={`${x}-${it}`}>{inline(it)}</li>)}</Tag>);
    list = null;
  };
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith("|")) {
      flush();
      const tbl = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) { tbl.push(lines[i]); i++; }
      const rows = tbl.filter((r) => !/^\s*\|[\s:|-]+\|\s*$/.test(r))
        .map((r) => r.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim()));
      const head = rows[0] || [], body = rows.slice(1);
      out.push(
        <div key={key++} className="overflow-x-auto my-4">
          <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
            <thead className="bg-slate-50"><tr>{head.map((h, x) => <th key={`h-${x}-${h}`} className="text-left font-semibold text-slate-600 px-3 py-2 border-b border-slate-200">{inline(h)}</th>)}</tr></thead>
            <tbody>{body.map((r, y) => <tr key={`r-${y}`} className="border-b border-slate-100 last:border-0">{r.map((c, x) => <td key={`c-${y}-${x}`} className="px-3 py-2 align-top text-slate-700">{inline(c)}</td>)}</tr>)}</tbody>
          </table>
        </div>
      );
      continue;
    }
    if (/^###\s+/.test(line)) { flush(); out.push(<h3 key={key++} className="font-head text-base font-semibold text-slate-800 mt-5 mb-1.5">{inline(line.replace(/^###\s+/, ""))}</h3>); i++; continue; }
    if (/^##\s+/.test(line)) { flush(); out.push(<h2 key={key++} className="font-head text-xl font-bold text-[#12346B] mt-8 mb-2 pb-1 border-b border-slate-200">{inline(line.replace(/^##\s+/, ""))}</h2>); i++; continue; }
    if (/^#\s+/.test(line)) { flush(); out.push(<h1 key={key++} className="font-head text-2xl font-bold text-slate-900 mb-1">{inline(line.replace(/^#\s+/, ""))}</h1>); i++; continue; }
    if (/^---\s*$/.test(line)) { flush(); out.push(<hr key={key++} className="my-6 border-slate-200" />); i++; continue; }
    if (/^>\s+/.test(line)) { flush(); out.push(<blockquote key={key++} className="border-l-4 border-[#F47920] bg-orange-50/50 px-4 py-2 my-3 text-slate-600 text-sm">{inline(line.replace(/^>\s+/, ""))}</blockquote>); i++; continue; }
    const um = line.match(/^\s*[-*]\s+(.*)/);
    const om = line.match(/^\s*\d+\.\s+(.*)/);
    if (um) { if (!list || list.type !== "ul") { flush(); list = { type: "ul", items: [] }; } list.items.push(um[1]); i++; continue; }
    if (om) { if (!list || list.type !== "ol") { flush(); list = { type: "ol", items: [] }; } list.items.push(om[1]); i++; continue; }
    if (line.trim() === "") { flush(); i++; continue; }
    flush();
    out.push(<p key={key++} className="my-2 text-slate-700 leading-relaxed text-sm">{inline(line)}</p>);
    i++;
  }
  flush();
  return out;
}

export default function Help() {
  const [md, setMd] = useState(null);
  useEffect(() => { api.get("/help/guide").then(({ data }) => setMd(data.content)).catch(() => setMd("# User Guide\n\nUnavailable.")); }, []);
  return (
    <div data-testid="help-page">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><BookOpen className="h-6 w-6 text-[#F47920]" /> Help & User Guide</h1>
          <p className="text-sm text-slate-500 mt-0.5">How to manage and use PCI PetroFlow ERP, end to end.</p>
        </div>
        <Button variant="outline" onClick={() => window.print()} data-testid="help-print"><Printer className="h-4 w-4 mr-1" /> Print / Save PDF</Button>
      </div>
      {md === null
        ? <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
        : <div className="bg-white border border-slate-200 rounded-lg p-6 md:p-8 max-w-4xl">{renderMarkdown(md)}</div>}
    </div>
  );
}
